/* Vue direction — détail d'un document (bloc 10c). Lecture seule : aucune */
/* section n'est éditable, il n'y a ni sauvegarde ni action d'archivage/publication. */

import { useEffect, useState } from 'react';
import { useParams } from '../lib/routing';
import { HeaderContent, HEADER_TITLE_CLASS } from '../components/shell/app-header';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import ContentSectionsEditor from '../components/content-sections-editor';
import { BookOpen, FileText, ListChecks, PencilSimple, WarningCircle } from '@phosphor-icons/react';
import {
  getMySchoolId,
  getSchoolContentDocument,
  listLevels,
  listSubjects,
  listTeachers,
  type Level,
  type SchoolContentDocumentDetail,
  type SchoolContentDocumentKind,
  type SchoolTeacher,
  type Subject,
} from '../lib/direction';

const KIND_LABEL: Record<SchoolContentDocumentKind, string> = {
  general: 'Document',
  course: 'Cours',
  exercice_sheet: 'Exercices',
  exam: 'Évaluation',
};

const KIND_ICON: Record<SchoolContentDocumentKind, typeof FileText> = {
  general: FileText,
  course: BookOpen,
  exercice_sheet: PencilSimple,
  exam: ListChecks,
};

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });

export default function DirectionContentDetail() {
  const { id } = useParams<{ id: string }>();
  const [doc, setDoc] = useState<SchoolContentDocumentDetail | null>(null);
  const [teachers, setTeachers] = useState<SchoolTeacher[]>([]);
  const [levels, setLevels] = useState<Level[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!id) { setLoading(false); return; }
    setLoading(true);
    setFailed(false);
    Promise.all([
      getSchoolContentDocument(id),
      getMySchoolId().then((sid) => (sid ? listTeachers(sid) : [])),
      listLevels(),
      listSubjects(),
    ])
      .then(([d, te, lv, sj]) => { setDoc(d); setTeachers(te); setLevels(lv); setSubjects(sj); })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [id]);

  const teacherName = (uid: string) => {
    const t = teachers.find((x) => x.user_id === uid);
    return t ? `${t.first_name} ${t.last_name}`.trim() || t.email || 'Enseignant' : 'Enseignant';
  };
  const levelLabel = (lid: string) => levels.find((l) => l.id === lid)?.label ?? null;
  const subjectLabel = (sid: string) => subjects.find((s) => s.id === sid)?.label ?? null;

  const Icon = doc ? KIND_ICON[doc.kind] : FileText;

  return (
    <div className="min-h-dvh bg-background flex flex-col">
      <HeaderContent width="md" showBurger backTo="/direction/contenus" backLabel="Contenus">
        {doc && (
          <div className="min-w-0">
            <div className="flex items-center gap-2.5 flex-wrap">
              <h1 className={HEADER_TITLE_CLASS}>{doc.title}</h1>
              <Badge variant="secondary" className="text-[12px] font-bold gap-1">
                <Icon size={12} weight="fill" />
                {KIND_LABEL[doc.kind]}
              </Badge>
            </div>
            <p className="m-0 mt-0.5 text-[13px] sm:text-[13.5px] leading-snug text-muted-foreground">
              {teacherName(doc.teacher_id)}
              {levelLabel(doc.level_id) && ` · ${levelLabel(doc.level_id)}`}
              {subjectLabel(doc.subject_id) && ` · ${subjectLabel(doc.subject_id)}`}
              {` · modifié le ${fmtDate(doc.updated_at)}`}
            </p>
          </div>
        )}
      </HeaderContent>
      <main className="max-w-[820px] mx-auto px-4 pb-16 pt-4 flex-1 w-full">
        {loading ? (
          <div className="flex flex-col gap-4">
            <Skeleton className="h-9 w-48 rounded-lg" />
            <Skeleton className="h-[120px] rounded-card" />
            <Skeleton className="h-[200px] rounded-card" />
          </div>
        ) : failed ? (
          <p className="flex items-center gap-2 text-[13.5px] text-[var(--naia-text-primary)] py-4">
            <WarningCircle size={18} weight="fill" className="text-destructive shrink-0" />
            Ce document n'a pas pu être chargé.
          </p>
        ) : !doc ? (
          <p className="text-muted-foreground py-4">Document introuvable.</p>
        ) : (
          <>
            {doc.global_correction_rules && (
              <div className="mb-4 p-3 rounded-[8px] bg-[var(--naia-sky)] text-[13px] text-[var(--naia-text-primary)]">
                <span className="font-bold">Consignes de correction — </span>
                {doc.global_correction_rules}
              </div>
            )}
            <ContentSectionsEditor documentId={doc.id} editable={false} initialSections={doc.sections} />
          </>
        )}
      </main>
    </div>
  );
}
